import { LitElement, html, nothing, type TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { loadScript } from "../../lib/load-script";
import { adaptProbabilityForm } from "../../lib/adapt-probability-form";
import { wireDonateFlow, GOFUNDME_SDK, GOFUNDME_CAMPAIGN } from "../../lib/donate-flow";
import { suppressNudgeTray } from "../../lib/suppress-nudge-tray";
import { startComponentTracking } from "../../lib/analytics-events";
import { trackEvent } from "../../lib/analytics";

// Stylesheet order is import order, and only the first two entries care about it:
// @font-face declarations, then the root reset. Every section rule after that is a
// class, so sections never depend on each other's position. Vite collects all of
// it into one emitted stylesheet and links it from the HTML.
import "../../styles/fonts.scss";
import "./driving-sweepstakes.scss";

import { renderNav } from "../nav/nav";
import { renderHero } from "../hero/hero";
import { renderSweepstakes } from "../sweepstakes/sweepstakes";
import { renderArtist } from "../artist/artist";
import { renderCollage } from "../collage/collage";
import { renderDonate } from "../donate/donate";
import { renderFooter } from "../footer/footer";

/** Sections in page order. `section=""` renders one of these on its own. */
const SECTIONS = ["nav", "hero", "sweepstakes", "artist", "collage", "donate", "footer"] as const;

type Section = (typeof SECTIONS)[number];

function isSection(value: string): value is Section {
  return (SECTIONS as readonly string[]).includes(value);
}

/**
 * Probability's hosted form component — the one thing on this page that is loaded
 * from someone else's server at runtime, because its field list lives in the
 * program config on their side. See `lib/load-script.ts` and the note in
 * `donate.ts`, and README "Third-party form" for the origins the CSP must allow.
 */
const FORM_SCRIPT = "https://api.helloprobability.io/js/universal_form_webcomponent.js";

/** The Valvoline sweepstakes program. Overridable via `program-id` / `env`. */
const DEFAULT_PROGRAM_ID = "6a63e3c49dff300066a28643";
const DEFAULT_ENV = "prod";

/**
 * The Valvoline x Blood Cancer United "Driving for a Dream" sweepstakes page:
 *
 *   <valvoline-driving-sweepstakes></valvoline-driving-sweepstakes>
 *
 * The only custom element in the project. The seven sections are plain template
 * functions (`src/components/<name>/<name>.ts`) composed here in page order, so
 * the whole public API — every attribute, every event — is declared in one file.
 *
 * Renders into the light DOM (`createRenderRoot` returns `this`). This is
 * load-bearing: Probability's form renders its own unprefixed markup and loads its
 * own stylesheet, and a shadow root would cut the two apart.
 *
 * On connect it loads Probability's form script, which upgrades the
 * `<universal-form-webcomponent>` in the donate section — the page's only
 * cross-origin request.
 *
 * Events, both bubbling and composed, dispatched from this element:
 *   - `valvoline-hero:play` — CustomEvent<undefined>
 *
 * The donation form does NOT emit an event: it is Probability's component and
 * submits to their API directly.
 */
@customElement("valvoline-driving-sweepstakes")
export class ValvolineDrivingSweepstakes extends LitElement {
  /** Where both CTAs point, opened in a new tab. Defaults to BCU's donation page. */
  @property({ type: String, attribute: "cta-href" })
  ctaHref = "https://givenow.bloodcancerunited.org/campaign/710629/donate";

  /** Overrides the bundled nav logo lockup. */
  @property({ type: String, attribute: "logo" })
  logo = "";

  /**
   * Hero video poster. Falls back to a sized FPO placeholder.
   *
   * Empty again since the v9 poster was deleted on 2026-08-24. It must not point
   * at a file that is not in `public/`: with a Widen embed the poster is unused,
   * so a stale path costs nothing today, but the moment `video-src` or the poster
   * variant is used again it renders as a broken image rather than the FPO box.
   */
  @property({ type: String, attribute: "poster" })
  poster = "";

  /** Hero video link. When omitted, the play button emits `valvoline-hero:play`. */
  @property({ type: String, attribute: "video-href" })
  videoHref = "";

  /**
   * A video file of our own, served from `public/`, used AHEAD of `video-embed`.
   *
   * Empty, and there is no file in the repo to point it at: the client supplied
   * the story as a Widen embed (see `video-embed`), and the self-hosted v9 cut
   * that lived here was deleted on 2026-08-24 so the deploy would stop carrying
   * 17 MB nothing served. The code path is kept, not the asset.
   *
   * Using it again means committing a new file, and three things about that one
   * are load-bearing — they are why the previous export worked:
   *
   *   - **Faststart** (`moov` before `mdat`), or the browser downloads the whole
   *     file before painting a frame;
   *   - a **web bitrate**. The v9 delivery file was ~17 MB at 1280x720; the
   *     master is 500 MB at 19 Mbps and must never be committed;
   *   - a **version in the filename**. `public/` is served unhashed under
   *     `Cache-Control: immutable`, so a new cut at an old URL keeps serving the
   *     old video to anyone who already watched it, for up to a year.
   *
   * Setting this also brings back the GA4 `video_*` events, which a framed embed
   * cannot report. See README.
   */
  @property({ type: String, attribute: "video-src" })
  videoSrc = "";

  /**
   * The hero video, and the default since 2026-08-24: the Palmer Goforth story
   * on Widen, as supplied by the client. It is their asset manager, so this is
   * the copy they control and re-publish; nothing about the video lives here.
   *
   * Used behind `video-src` and ahead of the poster. The origin is already in
   * domains.config.json — anything set here must be, or the CSP blocks the frame.
   *
   * **This costs the GA4 video events, and that is not a bug to fix here.**
   * `video_start`, `video_progress`, `video_pause` and `video_complete` are read
   * off a native `<video>` in our own document (analytics-events.ts). Widen frames
   * a share page and exposes no player API — no postMessage protocol, no SDK —
   * so there is nothing to listen to across the origin boundary. The events stop
   * for as long as the hero is a Widen embed. Only `section_view` still reports
   * that anyone reached the hero at all.
   *
   * Note also that this is a `/view/` share URL. Widen's stable permalink form is
   * `/content/`; a `/view/` link is tied to the share and can be revoked or
   * re-issued on their side, which would empty the frame with no deploy of ours.
   * Worth swapping for the `/content/` form if they will give it.
   */
  @property({ type: String, attribute: "video-embed" })
  videoEmbed =
    "https://valvoline.widen.net/view/video/krcm2dhzyb/GBL_VID_1709_EN_VGO_HONORED_HEROS_PALMER_GOFORTH_FULL_STORY.mp4?u=f0wzam";

  /** Overrides the bundled livery photo. */
  @property({ type: String, attribute: "livery" })
  livery = "";

  /** Palmer at work. Falls back to a sized FPO placeholder. */
  @property({ type: String, attribute: "photo" })
  photo = "";

  /** Overrides the bundled collage mantra note artwork. */
  @property({ type: String, attribute: "mantra-note" })
  mantraNote = "";

  /** Overrides the bundled Blood Cancer United wordmark. */
  @property({ type: String, attribute: "bcu-logo" })
  bcuLogo = "";

  /** Overrides the bundled footer "Not for nothing" lockup. */
  @property({ type: String, attribute: "footer-mantra" })
  footerMantra = "";

  /** Overrides the bundled footer signature. */
  @property({ type: String, attribute: "signature" })
  signature = "";

  /** Probability program the donation form submits to. */
  @property({ type: String, attribute: "program-id" })
  programId = DEFAULT_PROGRAM_ID;

  /** Probability environment for the form — `prod` or `stage`. */
  @property({ type: String, attribute: "env" })
  env = DEFAULT_ENV;

  /** GoFundMe campaign the checkout opens. */
  @property({ type: String, attribute: "gofundme-campaign" })
  gofundmeCampaign = GOFUNDME_CAMPAIGN;

  /**
   * Renders a single section instead of the whole page — for the isolated test
   * page at /example/. Not part of the public contract: an unknown or
   * absent value renders every section, so a typo cannot blank the page.
   */
  @property({ type: String, attribute: "section" })
  section = "";

  /** Set if the vendor form script never loaded, so the card can say so. */
  @state()
  private formFailed = false;

  /**
   * Whether the hero video has ever played.
   *
   * State rather than a DOM flag because it drives the render: our button covers
   * the whole poster only before the first play, and the browser's own controls
   * appear only after it. It never goes back to false — a pause is not a return
   * to the poster, and re-covering the video would swallow the clicks meant for
   * the controls bar.
   */
  @state()
  private heroVideoStarted = false;

  /**
   * Whether the hero video is paused right now — it decides whether the corner
   * button shows play or pause.
   *
   * Kept separate from `heroVideoStarted` because the two answer different
   * questions, and both are set from the element's own `play`/`pause` events
   * rather than from our click. That way the button is right even when playback
   * was driven from the browser's controls, the keyboard, or the end of the
   * video, none of which our click handler ever sees.
   */
  @state()
  private heroVideoPaused = true;

  protected override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();

    // One delegated listener rather than a handler threaded through both CTA
    // templates: nav and hero share `ctaHref`, and this also covers any in-page
    // link a section adds later.
    this.addEventListener("click", this.#onClick);

    // Deduplicated inside loadScript, so a second instance on the page costs
    // nothing — and never re-enters their customElements.define().
    loadScript(FORM_SCRIPT).catch(() => {
      this.formFailed = true;
      // Worth a hit of its own: with the form gone the page can still look
      // perfectly healthy in GA — sessions and scroll depth carry on — while no
      // entry on it is possible. This is what separates "nobody donated" from
      // "nobody could".
      trackEvent("form_unavailable", { form_id: "probability-universal-form", section: "donate" });
    });
  }

  /**
   * Scrolls to an in-page target without writing its hash into the URL.
   *
   * The CTAs stay real `<a href="#donate">` links: that is what makes them
   * focusable, gives them link semantics for assistive tech, shows a target on
   * hover, and still works if this script never runs. Only the default jump is
   * replaced — hence `preventDefault` rather than dropping the href.
   *
   * Deliberately narrow. It ignores anything that is not a bare same-page hash,
   * so `cta-href` pointed at a real URL still navigates, and GoFundMe's checkout
   * trigger (`href="?campaign=..."`) is never touched. Modified clicks are left
   * alone too, so open-in-new-tab keeps working.
   */
  #onClick = (event: MouseEvent): void => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = (event.target as Element | null)?.closest?.("a");
    const href = link?.getAttribute("href");
    if (!href || href.length < 2 || !href.startsWith("#")) return;

    const target = this.querySelector<HTMLElement>(href);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({
      // Honour the OS setting: an unrequested smooth scroll of a whole page is
      // exactly what that preference exists to stop.
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });

    // Without this the focus ring stays on the CTA, so the next Tab resumes at
    // the top of the page instead of at the form the visitor just jumped to —
    // the one thing the default hash jump does for free. `preventScroll` keeps
    // it from fighting the smooth scroll above.
    target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
  };

  protected override firstUpdated(): void {
    const slot = this.querySelector<HTMLElement>(".valvoline-donate__form");
    if (!slot) return;

    // The two Figma-card adaptations CSS cannot express — see the module.
    // Set up here rather than after the script resolves: it watches for the form,
    // so it does not care whether their render has happened yet.
    this.#stopAdapting = adaptProbabilityForm(slot);

    // Chains Probability's submit to GoFundMe's checkout — see lib/donate-flow.ts.
    this.#stopDonateFlow = wireDonateFlow(slot);

    // GA4 events that need the rendered page: which sections were reached, and
    // the hero video. The page-level ones (scroll depth, links, form start) are
    // started by the analytics entry, which both pages import.
    this.#stopTracking = startComponentTracking(this);

    // Armed BEFORE the SDK loads, so the tray is caught the moment it is
    // injected rather than after a frame of it being on screen. The stylesheet
    // hides it; this only closes the inline-!important hole — see the module.
    this.#stopSuppressingTray = suppressNudgeTray();

    // Loaded here and not in connectedCallback on purpose: their SDK scans the
    // page once for an anchor carrying `?campaign=`, so the trigger rendered by
    // donate.ts has to exist in the DOM before this runs.
    loadScript(GOFUNDME_SDK).catch(() => {
      // Nothing to show: the visible submit is Probability's, and donate-flow
      // falls back to posting the entry when the trigger cannot open a checkout.
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener("click", this.#onClick);
    this.#stopAdapting?.();
    this.#stopAdapting = undefined;
    this.#stopDonateFlow?.();
    this.#stopDonateFlow = undefined;
    this.#stopSuppressingTray?.();
    this.#stopSuppressingTray = undefined;
    this.#stopTracking?.();
    this.#stopTracking = undefined;
  }

  #stopAdapting?: () => void;
  #stopDonateFlow?: () => void;
  #stopSuppressingTray?: () => void;
  #stopTracking?: () => void;

  override render(): TemplateResult {
    const only = isSection(this.section) ? this.section : undefined;

    return html`
      ${this.#section("nav", only)} ${this.#section("hero", only)}
      ${this.#section("sweepstakes", only)} ${this.#section("artist", only)}
      ${this.#section("collage", only)} ${this.#section("donate", only)}
      ${this.#section("footer", only)}
    `;
  }

  #section(name: Section, only: Section | undefined): TemplateResult | typeof nothing {
    if (only && only !== name) return nothing;

    switch (name) {
      case "nav":
        return renderNav({ logo: this.logo, ctaHref: this.ctaHref });
      case "hero":
        return renderHero({
          poster: this.poster,
          videoHref: this.videoHref,
          videoSrc: this.videoSrc,
          videoEmbed: this.videoEmbed,
          videoStarted: this.heroVideoStarted,
          videoPaused: this.heroVideoPaused,
          onVideoPlaying: (playing: boolean) => {
            if (playing) this.heroVideoStarted = true;
            this.heroVideoPaused = !playing;
          },
          ctaHref: this.ctaHref,
          host: this,
        });
      case "sweepstakes":
        return renderSweepstakes({ livery: this.livery });
      case "artist":
        return renderArtist({ photo: this.photo });
      case "collage":
        return renderCollage({ mantraNote: this.mantraNote });
      case "donate":
        return renderDonate({
          bcuLogo: this.bcuLogo,
          programId: this.programId,
          env: this.env,
          formFailed: this.formFailed,
          gofundmeCampaign: this.gofundmeCampaign,
        });
      case "footer":
        return renderFooter({ mantra: this.footerMantra, signature: this.signature });
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "valvoline-driving-sweepstakes": ValvolineDrivingSweepstakes;
  }
}
